import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import {
  HiX, HiRefresh, HiPlay, HiPause, HiVolumeUp, HiVolumeOff,
  HiCog, HiArrowsExpand, HiStatusOnline,
} from 'react-icons/hi';
import {
  getCdnToken, needsCdnToken, isIpRestricted, isCorsAllowed,
  startTokenRefresh, isTokenExpired, onTokenUpdate, proxyUrl,
} from '../services/channelStream';

/**
 * Live TV player — YouTube-style watch page (left-aligned player, info below,
 * related channels on the right) with fully custom controls.
 * Supports HLS (hls.js) and DASH/DRM (shaka-player with ClearKey injection),
 * CDN token auto-refresh, quality selection, and retry.
 */
export default function ChannelPlayer({ channel, channels, onClose, onSelectChannel }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qualityLevels, setQualityLevels] = useState([]);
  const [currentQuality, setCurrentQuality] = useState(-1);
  const [retryCount, setRetryCount] = useState(0);
  const [tokenCountdown, setTokenCountdown] = useState(0);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hlsRef = useRef(null);
  const playerRef = useRef(null);
  const initRef = useRef(null);

  // Token refresh + countdown
  useEffect(() => {
    const stopRefresh = startTokenRefresh();
    setTokenCountdown(60);
    const countdownInterval = setInterval(() => {
      setTokenCountdown((prev) => (prev <= 1 ? 60 : prev - 1));
    }, 1000);
    const handleTokenUpdate = () => {
      setTokenCountdown(60);
      if (error && error.includes('Token expired')) {
        setError(null);
        setLoading(true);
        setRetryCount((prev) => prev + 1);
        if (initRef.current) initRef.current();
      }
    };
    onTokenUpdate(handleTokenUpdate);
    return () => {
      stopRefresh();
      clearInterval(countdownInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryCount((prev) => prev + 1);
    if (initRef.current) initRef.current();
  }, []);

  const isDash =
    channel.linkType === 'DASH' ||
    channel.linkType === 'DRM' ||
    (channel.streamUrl || '').endsWith('.mpd');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const v = video;
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError(null);
      setQualityLevels([]);
      setCurrentQuality(-1);

      const streamUrl = channel.streamUrl || '';
      const ipRestricted = isIpRestricted(streamUrl);
      const corsAllowed = isCorsAllowed(streamUrl);

      if (!isDash) {
        // ---- HLS ----
        const cdntoken = needsCdnToken(streamUrl) ? await getCdnToken() : '';
        let finalUrl;

        if (corsAllowed && cdntoken) {
          finalUrl = streamUrl + (streamUrl.includes('?') ? '&' : '?') + 'cdntoken=' + cdntoken;
        } else if (ipRestricted && cdntoken) {
          finalUrl = streamUrl + (streamUrl.includes('?') ? '&' : '?') + 'cdntoken=' + cdntoken;
        } else {
          finalUrl = proxyUrl(streamUrl, cdntoken);
        }

        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 90,
            maxBufferLength: 60,
            maxMaxBufferLength: 120,
            maxBufferSize: 120 * 1024 * 1024,
            maxBufferHole: 0.5,
            maxStarvationDelay: 4,
            maxLoadingDelay: 4,
            startLevel: -1,
            startFragPrefetch: true,
            progressive: true,
            xhrSetup: (xhr) => {
              if (ipRestricted) xhr.withCredentials = false;
            },
          });
          hlsRef.current = hls;
          hls.loadSource(finalUrl);
          hls.attachMedia(v);

          hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
            if (cancelled) return;
            const levels = data.levels.map((l) => ({ height: l.height, bitrate: l.bitrate }));
            setQualityLevels(levels);
            setLoading(false);
            v.play().catch(() => {});
          });

          hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
            setCurrentQuality(data.level);
          });

          let networkRetries = 0;
          hls.on(Hls.Events.ERROR, (_ev, data) => {
            if (cancelled) return;
            if (data.fatal) {
              if (data.type === Hls.ErrorTypes.NETWORK_ERROR && networkRetries < 3) {
                networkRetries++;
                setTimeout(() => { if (!cancelled) hls.startLoad(); }, 2000 * networkRetries);
              } else {
                setError(`HLS error: ${data.type} - ${data.details || ''}`);
                setLoading(false);
              }
            }
          });
        } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
          v.src = finalUrl;
          v.addEventListener('loadedmetadata', () => {
            if (!cancelled) { setLoading(false); v.play().catch(() => {}); }
          }, { once: true });
        } else {
          setError('HLS not supported in this browser.');
          setLoading(false);
        }
      } else {
        // ---- DASH / DRM (shaka-player) ----
        try {
          const shakaMod = await import('shaka-player/dist/shaka-player.compiled.js');
          if (cancelled) return;
          const shaka = shakaMod.default || shakaMod;
          shaka.polyfill.installAll();
          if (!shaka.Player.isBrowserSupported()) {
            setError('DASH/DRM playback not supported in this browser.');
            setLoading(false);
            return;
          }

          const player = new shaka.Player();
          playerRef.current = player;
          await player.attach(v);

          const cdntoken = needsCdnToken(streamUrl) ? await getCdnToken() : '';

          if (channel.drmKid && channel.drmKey) {
            player.configure({
              drm: {
                clearKeys: { [channel.drmKid]: channel.drmKey },
                preferredKeySystems: ['org.w3.clearkey'],
              },
            });
          }

          player.configure({
            streaming: {
              retryParameters: {
                maxAttempts: 5, baseDelay: 1000, backoffFactor: 2, fuzzFactor: 0.5, timeout: 30000,
              },
              bufferingGoal: 30,
              rebufferingGoal: 2,
              bufferBehind: 60,
              ignoreTextStreamFailures: true,
              stallEnabled: true,
              stallThreshold: 1,
            },
            abr: {
              enabled: true,
              defaultBandwidthEstimate: 800000,
            },
          });

          let mpdUrl;

          if (corsAllowed && cdntoken) {
            // Fetch MPD directly from CDN, strip DRM + inject ClearKey in-flight (no blob URLs).
            mpdUrl = streamUrl + (streamUrl.includes('?') ? '&' : '?') + 'cdntoken=' + cdntoken;

            player.getNetworkingEngine().registerRequestFilter((_type, request) => {
              if (request.uris) {
                request.uris = request.uris.map((uri) => {
                  if (
                    (uri.includes('azamtvltd.co.tz') || uri.includes('zapitv.com') || uri.includes('edgenextcdn.net')) &&
                    !uri.includes('cdntoken=')
                  ) {
                    return uri + (uri.includes('?') ? '&' : '?') + 'cdntoken=' + cdntoken;
                  }
                  return uri;
                });
              }
            });

            player.getNetworkingEngine().registerResponseFilter((_type, response, request) => {
              const uri = (request.uris && request.uris[0]) || '';
              const ct = (response.headers && (response.headers['content-type'] || response.headers['Content-Type'])) || '';
              const isManifest = uri.includes('.mpd') || ct.includes('dash') || ct.includes('xml');

              if (isManifest && response.data) {
                try {
                  const text = new TextDecoder().decode(response.data);
                  if (!text.includes('<MPD') && !text.includes('<?xml')) return;

                  let mpdText = text;

                  const sets = mpdText.match(/<AdaptationSet[^>]*>[\s\S]*?<\/AdaptationSet>/g) || [];
                  const hasKid = [];
                  const kids = [];
                  for (const block of sets) {
                    const kidMatch = block.match(/default_KID="([a-f0-9-]+)"/i);
                    hasKid.push(!!kidMatch);
                    kids.push(kidMatch ? kidMatch[1] : '');
                  }

                  mpdText = mpdText.replace(/<ContentProtection[^>]*>[\s\S]*?<\/ContentProtection>/g, '');
                  mpdText = mpdText.replace(/<ContentProtection[^>]*\/>/g, '');
                  mpdText = mpdText.replace(/<cenc:pssh[^>]*>[\s\S]*?<\/cenc:pssh>/g, '');

                  let setIdx = 0;
                  mpdText = mpdText.replace(/<AdaptationSet[^>]*>/g, (match) => {
                    const idx = setIdx++;
                    if (idx < hasKid.length && hasKid[idx]) {
                      const kid = kids[idx];
                      return (
                        match +
                        `<ContentProtection schemeIdUri="urn:mpeg:dash:mp4protection:2011" value="cenc" cenc:default_KID="${kid}"/>` +
                        `<ContentProtection schemeIdUri="urn:uuid:1077efec-c0b2-4d02-ace3-3c1e52e2fb4b" value="org.w3.clearkey"/>`
                      );
                    }
                    return match;
                  });

                  if (!hasKid.some((h) => h) && channel.drmKid) {
                    const kidUuid = channel.drmKid.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
                    mpdText = mpdText.replace(/<AdaptationSet[^>]*>/, (match) =>
                      match +
                      `<ContentProtection schemeIdUri="urn:mpeg:dash:mp4protection:2011" value="cenc" cenc:default_KID="${kidUuid}"/>` +
                      `<ContentProtection schemeIdUri="urn:uuid:1077efec-c0b2-4d02-ace3-3c1e52e2fb4b" value="org.w3.clearkey"/>`
                    );
                  }

                  response.data = new TextEncoder().encode(mpdText);
                } catch {}
              }
            });
          } else {
            // Other CDNs: route through the backend proxy (which strips DRM + adds ClearKey).
            mpdUrl = proxyUrl(streamUrl, cdntoken);
          }

          await player.load(mpdUrl);

          const tracks = player.getVariantTracks();
          if (tracks && tracks.length > 0) {
            setQualityLevels(
              tracks
                .map((t) => ({ height: t.height || 0, bitrate: t.bandwidth || 0 }))
                .sort((a, b) => b.height - a.height)
            );
          }

          player.addEventListener('error', (event) => {
            if (cancelled) return;
            const detail = event && event.detail;
            console.error('[Shaka Error]', detail && detail.category, detail && detail.code, detail && detail.message);
          });

          if (!cancelled) { setLoading(false); v.play().catch(() => {}); }
        } catch (e) {
          if (!cancelled) {
            const msg = e && e.message ? e.message : String(e);
            if (isIpRestricted(streamUrl) && (msg.includes('403') || msg.includes('MPD fetch failed'))) {
              setError(isTokenExpired() ? 'Token expired. Auto-refreshing every 60s - press Retry.' : `Stream error: ${msg}`);
            } else {
              setError(`DRM error: ${msg}`);
            }
            setLoading(false);
          }
        }
      }
    }

    initRef.current = init;
    init();

    return () => {
      cancelled = true;
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch {}
        hlsRef.current = null;
      }
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch {}
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, retryCount]);

  // Sync play/pause/mute/volume/fullscreen state with the <video> element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onVolume = () => { setIsMuted(v.muted); setVolume(v.volume); };
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', onVolume);
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVolume);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, []);

  const setHlsQuality = (levelIndex) => {
    const hls = hlsRef.current;
    if (!hls) return;
    hls.currentLevel = levelIndex;
    setCurrentQuality(levelIndex);
  };

  const setDashQuality = (trackIndex) => {
    const player = playerRef.current;
    if (!player) return;
    const tracks = player.getVariantTracks();
    if (trackIndex === -1) {
      player.configure({ abr: { enabled: true } });
    } else if (trackIndex < tracks.length) {
      player.configure({ abr: { enabled: false } });
      player.selectVariantTrack(tracks[trackIndex], true);
    }
    setCurrentQuality(trackIndex);
  };

  const currentQualityLabel =
    currentQuality === -1
      ? 'Auto'
      : qualityLevels[currentQuality]?.height
      ? `${qualityLevels[currentQuality].height}p`
      : 'Auto';

  // Custom control handlers
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  };

  const onVolumeChange = (val) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = val;
    v.muted = val === 0;
  };

  const toggleFullscreen = () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else wrap.requestFullscreen?.().catch(() => {});
  };

  const related = (channels || []).filter((c) => c._id !== channel._id);

  return (
    <div className="yt-overlay">
      <div className="yt-topbar">
        <div className="yt-topbar-brand">
          <HiStatusOnline size={20} />
          <span>Live TV</span>
        </div>
        <button className="yt-close-btn" onClick={onClose} title="Close">
          <HiX size={22} />
        </button>
      </div>

      <div className="yt-container">
        {/* Main column: player + info */}
        <div className="yt-main">
          <div className="yt-video-wrap" ref={wrapRef}>
            <video
              ref={videoRef}
              className="yt-video"
              autoPlay
              playsInline
              onClick={togglePlay}
            />

            {/* Custom controls */}
            <div className="yt-controls" onClick={(e) => e.stopPropagation()}>
              <button className="yt-ctrl-btn" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'}>
                {isPlaying ? <HiPause size={20} /> : <HiPlay size={20} />}
              </button>

              <div className="yt-volume">
                <button className="yt-ctrl-btn" onClick={toggleMute} title="Mute">
                  {isMuted || volume === 0 ? <HiVolumeOff size={20} /> : <HiVolumeUp size={20} />}
                </button>
                <input
                  className="yt-volume-slider"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => onVolumeChange(Number(e.target.value))}
                />
              </div>

              <span className="yt-live-badge"><span className="ch-live-dot" /> LIVE</span>

              <div className="yt-controls-spacer" />

              {qualityLevels.length > 1 && (
                <div className="ch-quality">
                  <button className="yt-ctrl-btn" onClick={() => setQualityOpen((o) => !o)} title="Quality">
                    <HiCog size={18} />
                  </button>
                  <div className="yt-quality-label">{currentQualityLabel}</div>
                  {qualityOpen && (
                    <div className="ch-quality-menu yt-quality-menu">
                      <button
                        className={currentQuality === -1 ? 'active' : ''}
                        onClick={() => { (isDash ? setDashQuality(-1) : setHlsQuality(-1)); setQualityOpen(false); }}
                      >
                        Auto
                      </button>
                      {qualityLevels.map((q, i) => (
                        <button
                          key={i}
                          className={currentQuality === i ? 'active' : ''}
                          onClick={() => { (isDash ? setDashQuality(i) : setHlsQuality(i)); setQualityOpen(false); }}
                        >
                          {q.height ? `${q.height}p` : `${Math.round(q.bitrate / 1000)}kbps`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button className="yt-ctrl-btn" onClick={handleRefresh} title="Refresh stream">
                <HiRefresh size={18} />
              </button>

              <button
                className={`yt-ctrl-btn${isFullscreen ? ' active' : ''}`}
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                <HiArrowsExpand size={18} />
              </button>
            </div>

            {loading && !error && (
              <div className="ch-player-buffering">
                <div className="loader-spinner" />
                <p>Loading stream...</p>
              </div>
            )}

            {error && (
              <div className="ch-player-error">
                <p>{error}</p>
                {error.includes('Token expired') && (
                  <div className="ch-token-countdown">
                    Auto-refreshing token in {tokenCountdown}s...
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button onClick={handleRefresh}><HiRefresh size={16} /> Retry</button>
                  <button onClick={onClose}>Go Back</button>
                </div>
              </div>
            )}
          </div>

          {/* Info section below the player (YouTube-style) */}
          <div className="yt-info">
            <h1 className="yt-title">{channel.name}</h1>
            <div className="yt-meta">
              <span className="yt-live-badge solid"><span className="ch-live-dot" /> LIVE</span>
              {channel.category && <span className="ch-card-cat">{channel.category}</span>}
              {(channel.linkType === 'DRM' || channel.linkType === 'DASH') && (
                <span className="ch-card-type">{channel.linkType}</span>
              )}
              {channel.requiresPayment && <span className="yt-premium">PREMIUM</span>}
            </div>
            {channel.description && <p className="yt-desc">{channel.description}</p>}
          </div>
        </div>

        {/* Sidebar: related channels */}
        {related.length > 0 && (
          <div className="yt-sidebar">
            <h3 className="yt-sidebar-title">More channels</h3>
            {related.map((c) => (
              <div
                key={c._id}
                className="yt-related-item"
                onClick={() => onSelectChannel?.(c)}
              >
                <div className="yt-related-thumb">
                  {c.thumbnail ? (
                    <img src={c.thumbnail} alt={c.name} loading="lazy" />
                  ) : (
                    <div className="yt-related-placeholder"><HiStatusOnline size={20} /></div>
                  )}
                  <span className="yt-related-live">LIVE</span>
                </div>
                <div className="yt-related-info">
                  <h4>{c.name}</h4>
                  <span>{c.category}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
